import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import apiClient, { handleApiError } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  ComplianceIncidentFilters,
  ComplianceIncidentReport,
  ComplianceIncidentReportListItem,
  CreateComplianceIncidentPayload,
  EscalateNearMissPayload,
  MarkNotifiedPayload,
  PaginatedResponse,
  StartInvestigationPayload,
} from '@/lib/types';

const BASE = '/api/v1/compliance/incidents';

function cleanFilters(filters: ComplianceIncidentFilters): Record<string, string> {
  const params: Record<string, string> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params[key] = String(value);
    }
  });
  return params;
}

// ─── Queries ────────────────────────────────────────────────────────────────

export function useComplianceIncidents(filters: ComplianceIncidentFilters = {}) {
  const cleaned = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.complianceIncidents.list(cleaned),
    queryFn: async () => {
      const { data } = await apiClient.get<PaginatedResponse<ComplianceIncidentReportListItem>>(
        `${BASE}/`,
        { params: cleaned },
      );
      return data;
    },
  });
}

export function useComplianceIncident(id: number) {
  return useQuery({
    queryKey: queryKeys.complianceIncidents.detail(id),
    queryFn: async () => {
      const { data } = await apiClient.get<ComplianceIncidentReport>(`${BASE}/${id}/`);
      return data;
    },
    enabled: !!id,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────────────

export function useCreateComplianceIncident() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateComplianceIncidentPayload) => {
      const { data } = await apiClient.post<ComplianceIncidentReport>(`${BASE}/`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceIncidents.all });
    },
    onError: handleApiError,
  });
}

export function useStartInvestigation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      incidentId,
      ...payload
    }: StartInvestigationPayload & { incidentId: number }) => {
      const { data } = await apiClient.post<ComplianceIncidentReport>(
        `${BASE}/${incidentId}/start-investigation/`,
        payload,
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.complianceIncidents.detail(variables.incidentId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceIncidents.lists() });
    },
    onError: handleApiError,
  });
}

export function useCompleteInvestigation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (incidentId: number) => {
      const { data } = await apiClient.post<ComplianceIncidentReport>(
        `${BASE}/${incidentId}/complete-investigation/`,
      );
      return data;
    },
    onSuccess: (_data, incidentId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.complianceIncidents.detail(incidentId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceIncidents.lists() });
    },
    onError: handleApiError,
  });
}

export function useMarkNotified() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      incidentId,
      ...payload
    }: MarkNotifiedPayload & { incidentId: number }) => {
      const { data } = await apiClient.post(`${BASE}/${incidentId}/mark-notified/`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceIncidents.all });
    },
    onError: handleApiError,
  });
}

export function useEscalateNearMiss() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: EscalateNearMissPayload) => {
      const { data } = await apiClient.post<ComplianceIncidentReport>(
        `${BASE}/escalate-near-miss/`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceIncidents.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.nearMisses.all });
    },
    onError: handleApiError,
  });
}
